import { useViewerDecks, useViewerQuery } from "queries/viewerQuery";
import React from "react";
import { isDevelopmentMode } from "tools/device";
import { FIVEHINT, STARTID } from "root/main/main/Main";
import TutorialContent, { HintPosition, TutorialFC } from "./TutorialContent";
import { useMatch, useLocation } from "react-router";
import { useMatchMain } from "root/RootRouter";
import FirstFewCards, { FIRSTFEWHINT } from "./steps/FirstFewCards";
import { SOURCE } from "root/library/library/libraryUrls";
import LongTouch from "./steps/LongTouch";
import { StartLearning } from "./steps/StartLearning";
import { FIRSTLOOPDONE } from "root/main/learn/Learn";
import { CARD, DECK, LEARN } from "root/main/MainRouter";
import PetMammoth, { PETMAMMOTH } from "./steps/PetMammoth";
import TapCard, { TAPTUTORIALDONE } from "./steps/TapCard";
import ThenSwipeAllRight from "./steps/ThenSwipeAllRight";
import { CARDSID } from "root/main/learn/Cards";
import Verdict, { SWIPINGDONE, TUTORIALDONE } from "./steps/Verdict";
import { VOICEHINT } from "components/other/HintTooltip";
import { useSeeHint } from "components/hint/Hint";
import Notifications, { NOTIFICATIONSDONE } from "root/profile/Notifications";
import SettingsDone from "./setting/SettingsDone";
import { LIKEITHINT } from "root/main/main/LikeIt";
import { useEvent, usePrevious } from "react-use";
import { PROFILE, VERIFY, ONBOARDING, LATE_REGISTRATION } from "../RootRouter.config";
import FeaturesHint from "./setting/FeaturesHint";
import useOnboardingStepTracker from "./onboarding/useOnboardingStepTracker";
import { useGetState } from "components/ReduxProvider";
import { useBlockOnboardingAfterCompletion, useOnboardingInitialAndForceRedirect } from "./onboardingRedirects";
import { useLogout } from "queries/users/logout";
import { useDeleteUserMutation } from "queries/users/deleteUser";
import { MOTIVATIONHINT } from "sharedJs__generated/constants";
import { isApp } from "tools/device";
import useOnboardingData from "hooks/useOnboardingData";
import { useOnboardingCompleted } from "hooks/useOnboardingCompleted";
import { useCreateABTest } from "queries/users/useCreateABTest";
import { OnboardingPlanWithDialog } from "./onboarding/OnboardingPlan";
import { useABTest } from "queries/users/useABTest";
export const FIRSTCARDSCOUNT = 3;

const Tutorial: React.FC<{}> = () => {
  const { hintWasSeen, isGuest, viewer } = useViewerQuery();
  const { deck } = useViewerDecks();
  const inProfile = useMatch(PROFILE.url() + "/*");
  const inSource = useMatch(SOURCE.query());
  const inMain = useMatchMain({ end: true });
  const inLearn = useMatch(LEARN.url());
  const inDeck = useMatch(DECK.url());
  const inVerify = useMatch(VERIFY.url());
  const inOnboarding = useMatch(ONBOARDING.url());
  const inCard = useMatch(CARD.url());
  const inLateRegistration = useMatch(LATE_REGISTRATION.url());
  const seeHint = useSeeHint();
  const deviceUsersDialogOpen = useGetState("deviceUsersDialogOpen");
  const location = useLocation();
  const previousLocation = usePrevious(location);
  const logout = useLogout();
  const deleteUser = useDeleteUserMutation();
  const { getOnboardingData } = useOnboardingData();
  const { notificationsPending } = getOnboardingData();
  const hasOnboardingCompleted = useOnboardingCompleted();
  const getNoPetMammoth = useCreateABTest("abtest_onDemand_noPetMammoth");
  const onboardPlan = useABTest("abtest_onboardPlan");

  useOnboardingStepTracker();
  useBlockOnboardingAfterCompletion();

  React.useEffect(() => {
    const isLeavingOnboarding = previousLocation?.pathname === ONBOARDING.url() && location.pathname === "/";
    if (!isLeavingOnboarding || hasOnboardingCompleted) return;

    const handleLogout = async () => {
      if (isGuest) {
        await deleteUser(false);
      }
      await logout();
    };
    handleLogout();
  }, [location, previousLocation, logout, deleteUser, isGuest, hasOnboardingCompleted]);

  const shouldSkipTutorial =
    hintWasSeen(TUTORIALDONE) ||
    hintWasSeen(VOICEHINT) ||
    inDeck ||
    inProfile ||
    inVerify ||
    inCard ||
    inLateRegistration ||
    deviceUsersDialogOpen;

  const shouldSkipOnboarding = !!shouldSkipTutorial || hasOnboardingCompleted;

  const initialRedirectDone = useOnboardingInitialAndForceRedirect(shouldSkipOnboarding);

  // finishing tutorial on ctrl+D
  const finishTutorial = (e) => {
    if (!isDevelopmentMode()) return;
    if ((e.metaKey || e.ctrlKey) && e.code === "KeyD" && !hintWasSeen(TUTORIALDONE)) {
      e.preventDefault();
      seeHint(TUTORIALDONE);
    }
  };
  useEvent("keydown", finishTutorial);

  React.useEffect(() => {
    if (!hintWasSeen(TUTORIALDONE) && (hintWasSeen(VOICEHINT) || hintWasSeen(LIKEITHINT))) {
      seeHint(TUTORIALDONE); // some historic users
    }
  }, [deck, hintWasSeen, seeHint]);

  const config: {
    condition: () => any; // booleanish
    Component: TutorialFC;
    position?: HintPosition;
    visibleId?: string;
    movingMammoth?: boolean;
  }[] = [
    {
      condition: () => !hintWasSeen(FIRSTFEWHINT) && inSource,
      Component: FirstFewCards
    },
    {
      condition: () => inSource && !hintWasSeen(FIVEHINT),
      Component: LongTouch
    },
    {
      condition: () => !hintWasSeen(PETMAMMOTH) && getNoPetMammoth() !== true,
      Component: PetMammoth
    },
    {
      condition: () => inMain && deck?.stats.unknown && !hintWasSeen(FIRSTLOOPDONE),
      Component: StartLearning,
      position: "bottom",
      visibleId: STARTID
    },
    {
      condition: () => inLearn && !hintWasSeen(TAPTUTORIALDONE),
      Component: TapCard,
      position: "bottom"
    },
    {
      condition: () => inLearn && !hintWasSeen(FIRSTLOOPDONE),
      Component: ThenSwipeAllRight,
      visibleId: CARDSID,
      position: "bottom"
    },
    ...(onboardPlan
      ? [
          {
            condition: () => !hintWasSeen(SWIPINGDONE),
            Component: OnboardingPlanWithDialog,
            position: "full" as HintPosition
          }
        ]
      : [{ condition: () => !hintWasSeen(SWIPINGDONE), Component: Verdict }]),

    {
      condition: () =>
        isApp() &&
        !hintWasSeen(NOTIFICATIONSDONE) &&
        !viewer?.fcmId &&
        notificationsPending /* when user from webfunnel -> pending notifications (but coach probably 2 because 2 is default) */,
      Component: (p) => <Notifications {...p} inTutorial style={{ minHeight: "calc(100% - 60px)" }} />,
      position: "full"
    },
    {
      condition: () => !hintWasSeen(MOTIVATIONHINT),
      Component: FeaturesHint,
      position: "full"
    },
    {
      condition: () => true,
      Component: SettingsDone,
      position: "full"
    }
  ];

  const index = inOnboarding ? 0 : config.findIndex((cnf) => cnf.condition());
  const c = React.useMemo(() => config[index], [index]); // eslint-disable-line react-hooks/exhaustive-deps

  const settingsStartIndex =
    (onboardPlan
      ? config.findIndex((c) => c.Component === OnboardingPlanWithDialog)
      : config.findIndex((c) => c.Component === Verdict)) + 1;
  const fromIndex = 0;
  const startPercentage = 0; // prepared if we decide to start onboarding from somewhere else
  const remainingPercentage = 100 - startPercentage;
  const perc = `${startPercentage + (remainingPercentage / (settingsStartIndex - fromIndex)) * (index - fromIndex)}%`;
  const showProgressbar = settingsStartIndex > index && index >= fromIndex;

  if (
    !c ||
    shouldSkipTutorial ||
    !initialRedirectDone ||
    inOnboarding ||
    !deck /* update for users who accidentally had their deck removed in onboarding */
  ) {
    return null;
  }

  return (
    <TutorialContent
      visibleId={c.visibleId}
      perc={perc}
      step={index}
      showProgressbar={showProgressbar}
      position={c.position}
      Component={c.Component}
    />
  );
};

export default Tutorial;
